import { html, customElement, classMap } from '@adobe/edex/ui/edex-lit';
import { EdexTagName, QueryParam, ResourceBuilderPath } from '@adobe/edex/ui/shared/constants';
import { BaseAuthElement } from '../../base.element';
import { EdexDomID } from '@adobe/edex/ui/shared/constants';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { MarshalledResource, DialogState, EntityStatus, ConfirmationDialogParams } from '@adobe/edex/ui/types';
import { selectDialog, selectResource } from '@adobe/edex/ui/store/selectors';
import * as routines from '@adobe/edex/ui/store/routines';
import { $t } from '@adobe/edex/ui/shared/config/i18n';

import '@spectrum-web-components/icons-workflow/icons/sp-icon-publish-remove';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-publish-check';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-edit';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-settings';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-user-admin.js';
import '@adobe/edex/ui/shared/components/dialog-next/dialog';

import('@adobe/edex/ui/components/resource/resource-action-bar/resource-action-bar.scss');

interface ResourceActionBarItem {
  id: EdexDomID;
  label?: string;
  icon: any;
  clicked?: boolean;
  onClick?: (e: any) => any;
  show?: boolean;
}

@customElement(EdexTagName.resourceActionBar)
export class ResourceActionBar extends BaseAuthElement {
  get useLightDom() {
    return true;
  }

  static get properties() {
    return {
      ...super.properties,
      resource: { attribute: false },
      dialog: { attribute: false },
      expandAdminPanel: { type: Boolean },
    };
  }
  resource: MarshalledResource;
  dialog: DialogState;
  device: string;
  expandAdminPanel: boolean;

  actions = {
    deleteResource: routines.deleteResource.trigger,
    updateResource: routines.updateResource.trigger,
    openDialog: routines.openDialog.trigger,
    closeDialog: routines.closeDialog.trigger,
  };

  connectedCallback() {
    super.connectedCallback();
    this.expandAdminPanel = false;
    this.device = this.getAttribute('device');
  }

  stateChanged(state) {
    super.stateChanged(state);
    this.resource = selectResource(state);
    this.dialog = selectDialog(state);
  }

  get showBar() {
    return this.isOwner(this.resource) || this.isAdmin;
  }

  get canEdit(): boolean {
    return this.isOwner(this.resource) || this.isAdmin;
  }

  get isPublished() {
    return this.resource?.status === EntityStatus.active;
  }

  get deviceType() {
    return this.device;
  }

  getDialogOpener = (id: EdexDomID) => () => {
    this.actions.openDialog({ id });
  };
  isDialogOpen(id: EdexDomID) {
    return this.dialog?.id === id;
  }

  get items(): ResourceActionBarItem[] {
    const {
      resource: { vanityURL },
      canEdit,
      isPublished,
      isAdmin,
    } = this;
    return [
      {
        id: EdexDomID.resourceSettings,
        label: 'Settings' as I18nKey,
        icon: html`
          <sp-icon-settings></sp-icon-settings>
        `,
        onClick: this.getDialogOpener(EdexDomID.resourceSettingsDialog),
        show: isAdmin,
      },
      {
        id: EdexDomID.edit,
        label: I18nKey.edit,
        icon: html`
          <sp-icon-edit></sp-icon-edit>
        `,
        onClick: () => {
          this.navigate({
            path: ResourceBuilderPath.edit,
            queryParams: { [QueryParam.resourceEditID]: vanityURL },
          });
        },
        show: canEdit,
      },

      {
        id: EdexDomID.publish,
        label: isPublished ? I18nKey.unpublish : I18nKey.draft,
        icon: isPublished
          ? html`
              <sp-icon-publish-remove></sp-icon-publish-remove>
            `
          : html`
              <sp-icon-publish-check></sp-icon-publish-check>
            `,
        clicked: isPublished,
        onClick: isPublished ? this.getDialogOpener(EdexDomID.resourceUnpublishConfirmationDialog) : this.handlePublish,
        show: canEdit,
      },

      {
        id: EdexDomID.delete,
        label: I18nKey.delete,
        icon: html`
          <sp-icon-delete></sp-icon-delete>
        `,
        onClick: this.getDialogOpener(EdexDomID.resourceDeleteConfirmationDialog),
        show: canEdit,
      },
    ].filter(({ show = true }) => show);
  }

  get roleText(): I18nKey {
    return this.isOwner(this.resource) ? I18nKey.author : I18nKey.admin;
  }
  async handleDelete() {
    this.actions.deleteResource({ id: this.resource.id });
  }

  firstUpdated() {
    this.expandAdminPanel = false;
  }

  handlePublish() {
    const { resource, isPublished, actions } = this;
    actions.updateResource({
      id: resource.id,
      data: {
        status: isPublished ? EntityStatus.inactive : EntityStatus.active,
      },
      onSuccess: () => {
        if (!isPublished) {
          this.getDialogOpener(EdexDomID.resourcePublishedConfirmationDialog)();
        }
      },
    });
    actions.closeDialog();
  }

  toggleAdminPanel() {
    this.expandAdminPanel = !this.expandAdminPanel;
  }

  renderConfirmationDialog({
    id,
    title,
    subtext,
    body,
    confirmLabel,
    onConfirm,
    confirmBtnVariant = 'cta',
  }: ConfirmationDialogParams) {
    if (!this.isDialogOpen(id)) {
      return null;
    }
    return html`
      <edex-dialog-next id="${id}" .header=${title} open @close="${this.actions.closeDialog}">
        <div class="confirmation-dialog" slot="content">
          <p class="confirmation-subtext">${$t(subtext)}</p>
          <p class="confirmation-body">${$t(body)}</p>
        </div>
        <div class="confirmation-dialog-actions" slot="footer">
          <sp-button variant="secondary" class="cancelButton" @click="${this.actions.closeDialog}">
            ${$t(I18nKey.noCancel)}
          </sp-button>
          <sp-button variant=${confirmBtnVariant} @click="${onConfirm}">${$t(confirmLabel)}</sp-button>
        </div>
      </edex-dialog-next>
    `;
  }

  renderUnpublishConfirmationDialog = () => {
    return this.renderConfirmationDialog({
      id: EdexDomID.resourceUnpublishConfirmationDialog,
      title: I18nKey.unpublishContentTitle,
      subtext: I18nKey.unpublishContentSubtext,
      body: I18nKey.unpublishContentBody,
      confirmLabel: I18nKey.unpublishContentConfirmBtn,
      onConfirm: this.handlePublish,
    });
  };

  renderSettingsDialog = () => {
    if (!this.isDialogOpen(EdexDomID.resourceSettingsDialog)) {
      return null;
    }
    return html`
      <edex-resource-settings-dialog .resource="${this.resource}"></edex-resource-settings-dialog>
    `;
  };

  renderDeleteConfirmationDialog = () => {
    return this.renderConfirmationDialog({
      id: EdexDomID.resourceDeleteConfirmationDialog,
      title: I18nKey.resourceDeletionConfirmTitle,
      subtext: I18nKey.deletionResourceConfirmSubtext,
      confirmLabel: I18nKey.deletionConfirmBtn,
      onConfirm: this.handleDelete,
      confirmBtnVariant: 'negative',
    });
  };

  renderResourceActionBarItem({ id, icon, label, onClick }: ResourceActionBarItem) {
    return html`
      <div data-id="${id}" class="resource-action-bar-item" @click="${onClick}">
        <sp-action-button class="role-button" @click="${onClick}">
          <sp-icon slot="icon">${icon}</sp-icon>
          <span>${$t(label)}</span>
        </sp-action-button>
      </div>
    `;
  }

  renderResourceFloatingControlPanelItems = ({ id, icon, label, onClick }: ResourceActionBarItem) => {
    return html`
      <div
        data-id="${id}"
        class="admin-control-btn ${classMap({ 'control-item': !this.expandAdminPanel })}"
        @click="${onClick}"
      >
        <sp-action-button class="role-button" @click="${onClick}">
          <sp-icon slot="icon">${icon}</sp-icon>
        </sp-action-button>
      </div>
    `;
  };

  render() {
    const { resource, showBar, expandAdminPanel } = this;
    if (!resource || !showBar) {
      return null;
    }

    return html`
      <sp-action-bar variant="fixed" open>
        <div class="role">
          <sp-icon-user-admin></sp-icon-user-admin>
          <span>${$t(this.roleText)}</span>
        </div>
        <div class="resource-action-bar-items">
          ${this.items.map(this.renderResourceActionBarItem)}
          ${this.renderSettingsDialog()}${this.renderUnpublishConfirmationDialog()}${this.renderDeleteConfirmationDialog()}
        </div>
      </sp-action-bar>
      <div class="mobile-admin-panel ${classMap({ expand: expandAdminPanel })}">
        ${this.items.map(this.renderResourceFloatingControlPanelItems)}
        <div
          class=" ${classMap({
            'admin-control-btn': this.expandAdminPanel,
            'control-toggle-collapse': !this.expandAdminPanel,
          })} "
        >
          ${expandAdminPanel
            ? html`
                <div class="divider"></div>
              `
            : null}
          <sp-action-button class="role-button" @click="${this.toggleAdminPanel}">
            <sp-icon-user-admin></sp-icon-user-admin>
          </sp-action-button>
        </div>
      </div>
    `;
  }
}
